// Backend call for New roles on Home.

import type { User } from "firebase/auth";
import { auth } from "../firebase";
import type { FeedRole, RoleCounts, RoleScope, RoleWindow } from "./newRoles";

const API_BASE = process.env.REACT_APP_API_URL ?? "http://localhost:5050";

// The last result, so Home after a refresh and the See all page show roles at
// once while a fresh copy loads. In memory for this visit, and in
// localStorage (this browser only) for the next. Kept with the job search
// settings it was matched for: after they change, it's never shown.
// A new name when what's saved can't be trusted: 2 = lists from before keyword matches were stopped after a wake
const STORAGE_KEY = "caramel.newRoles.v2";
/** Younger than this, a page change reuses it without asking the backend again */
export const FRESH_MS = 2 * 60000;
/**
 * Older than this, a saved copy isn't shown at all. A few days old still
 * beats an empty card while the backend wakes up; the card says it's updating.
 */
const MAX_AGE_MS = 7 * 24 * 3600000;

/**
 * The roles sent (the best ones, up to the backend's limit), each window's
 * count over every match, and the windows with more matches than were sent
 */
export type RolesFeed = { roles: FeedRole[]; counts?: RoleCounts; capped?: RoleWindow[] };
type Saved = RolesFeed & { uid: string; targets: string; at: number };
const memory: Partial<Record<RoleScope, Saved>> = {};
const storageKey = (scope: RoleScope) => (scope === "all" ? `${STORAGE_KEY}.all` : STORAGE_KEY);

/**
 * The users/{uid} fields the backend matches on (backend/job_matching.py
 * Preferences.from_user), as one string: roles saved for other ones are stale.
 */
export function targetsKey(user: Record<string, unknown> | undefined): string {
  const { roles, jobTypes, locations, careerStage, degree } = user ?? {};
  return JSON.stringify([roles ?? null, jobTypes ?? null, locations ?? null, careerStage ?? null, degree ?? null]);
}

function savedCopy(uid: string, scope: RoleScope, targets: string): Saved | undefined {
  const fits = (s: Saved | undefined) => s?.uid === uid && s.targets === targets;
  if (fits(memory[scope])) return memory[scope];
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey(scope)) || "null");
    if (fits(parsed) && Array.isArray(parsed.roles)) return (memory[scope] = parsed);
  } catch {
    // Storage blocked or bad JSON: just load
  }
  return undefined;
}

const HOME_WINDOWS: RoleWindow[] = ["today", "3days", "week"];
const WEEK_MS = 7 * 24 * 3600000;

/** See all's copy cut down to Home's: the last week and its windows */
function asHomeCopy(all: Saved): Saved {
  const pick = <T,>(o: Partial<Record<RoleWindow, T>> | undefined) =>
    o && Object.fromEntries(Object.entries(o).filter(([w]) => HOME_WINDOWS.includes(w as RoleWindow)));
  return {
    ...all,
    roles: all.roles.filter((r) => all.at - (Date.parse(r.listedAt) || 0) <= WEEK_MS),
    counts: pick(all.counts),
    capped: all.capped?.filter((w) => HOME_WINDOWS.includes(w)),
  };
}

/**
 * The last roles loaded for this user, scope and job search settings
 * (targetsKey), and how old they are. Home takes See all's copy when it's
 * newer (it holds Home's roles too), so going back from See all shows the
 * same numbers instead of an older Home copy.
 */
export function cachedNewRoles(
  uid: string,
  scope: RoleScope,
  targets: string,
): (RolesFeed & { ageMs: number }) | null {
  let saved = savedCopy(uid, scope, targets);
  if (scope === "new") {
    const all = savedCopy(uid, "all", targets);
    if (all && (!saved || all.at > saved.at)) saved = asHomeCopy(all);
  }
  if (!saved) return null;
  const ageMs = Date.now() - saved.at;
  return ageMs < MAX_AGE_MS ? { roles: saved.roles, counts: saved.counts, capped: saved.capped, ageMs } : null;
}

/** Take a role out of the saved copies (Applied), so a refresh doesn't show it again before the next load. */
export function forgetRole(id: string): void {
  for (const scope of ["new", "all"] as RoleScope[]) {
    try {
      const saved: Saved | undefined =
        memory[scope] ?? JSON.parse(localStorage.getItem(storageKey(scope)) || "null") ?? undefined;
      if (!saved?.roles?.some((r) => r.id === id)) continue;
      const next = { ...saved, roles: saved.roles.filter((r) => r.id !== id) };
      memory[scope] = next;
      localStorage.setItem(storageKey(scope), JSON.stringify(next));
    } catch {
      // Storage blocked or bad JSON: the memory copy (if any) is enough
    }
  }
}

/**
 * "Applied" on a role: the backend saves the card, or joins the card already
 * there for it (the confirmation email came first). backend/app.py role_applied.
 */
export async function markRoleApplied(card: Record<string, unknown>): Promise<{ cardId: string; merged: boolean }> {
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in");
  const response = await fetch(`${API_BASE}/roles/applied`, {
    method: "POST",
    headers: { Authorization: `Bearer ${await user.getIdToken()}`, "Content-Type": "application/json" },
    body: JSON.stringify({ card }),
  });
  if (!response.ok) throw new Error(`Applied failed: ${response.status}`);
  return response.json();
}

const STARTING_RETRIES = 6;
const STARTING_WAIT_MS = 10000;

/**
 * The matched roles for the signed-in user (backend/app.py roles_feed): the
 * last week, or every open one. `targets`: their job search settings now
 * (targetsKey), saved with the result.
 */
export function fetchNewRoles(scope: RoleScope, targets: string): Promise<RolesFeed> {
  const user = auth.currentUser;
  if (!user) return Promise.resolve({ roles: [] });
  // The same load already on its way (onboarding started it): wait for that one
  const key = JSON.stringify([user.uid, scope, targets]);
  const running = loading.get(key);
  if (running) return running;
  const load = loadNewRoles(user, scope, targets).finally(() => loading.delete(key));
  loading.set(key, load);
  return load;
}

const loading = new Map<string, Promise<RolesFeed>>();

async function loadNewRoles(user: User, scope: RoleScope, targets: string): Promise<RolesFeed> {
  const url = `${API_BASE}/roles/feed${scope === "all" ? "?scope=all" : ""}`;
  let response = await fetch(url, { headers: { Authorization: `Bearer ${await user.getIdToken()}` } });
  // The backend is still starting (503): stay on loading, it's a moment
  for (let tries = 0; response.status === 503 && tries < STARTING_RETRIES; tries++) {
    await new Promise((resolve) => setTimeout(resolve, STARTING_WAIT_MS));
    response = await fetch(url, { headers: { Authorization: `Bearer ${await user.getIdToken()}` } });
  }
  if (!response.ok) throw new Error(`New roles failed: ${response.status}`);
  const body = await response.json();
  const roles: FeedRole[] = Array.isArray(body?.roles) ? body.roles : [];
  const counts: RoleCounts | undefined = body?.counts && typeof body.counts === "object" ? body.counts : undefined;
  const capped: RoleWindow[] | undefined = Array.isArray(body?.capped) ? body.capped : undefined;
  const saved = (memory[scope] = { uid: user.uid, targets, at: Date.now(), roles, counts, capped });
  try {
    localStorage.setItem(storageKey(scope), JSON.stringify(saved));
  } catch {
    // Full or blocked: the memory copy still helps this visit
  }
  return { roles, counts, capped };
}
